import { ChangeDetectionStrategy, Component, Input } from "@angular/core";

/**
 * Product brand lockup.
 * - GPS uses the full CDN wordmark SVG (`logo_rubbl_gps.svg`).
 * - Connect uses the full CDN color PNG (`rubbl_connect_color.png`) on light backgrounds,
 *   and the reverse PNG (`rubbl_connect_color_reverse.png`, white "RUBBL") when `[light]`
 *   is set for dark backgrounds.
 * - Shop and Rewards use their supplied wordmark artwork.
 * - Other products use the Rubbl icon + "RUBBL {product}" HTML lockup (`[light]` turns
 *   the "RUBBL" text white).
 */
@Component({
  selector: "common-brand-logo",
  templateUrl: "./brand-logo.component.html",
  styleUrls: ["./brand-logo.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandLogoComponent {
  /** Product name, e.g. "GPS" or "Connect". */
  @Input() product = "";

  /**
   * Set on dark backgrounds. For Connect, swaps in the reverse (white-RUBBL) wordmark.
   * For HTML lockups, renders "RUBBL" in white instead of black.
   */
  @Input() light = false;

  /** Wordmark height in px; the icon / full logo scales with it. */
  @Input() size = 28;

  private readonly productLogos: Record<string, string> = {
    gps: "https://company.rubbl.com/pubweb/marketplace/images/logos/logo_rubbl_gps.svg",
    connect: "https://company.rubbl.com/pubweb/marketplace/images/logos/rubbl_connect_color.png",
    rewards: "/assets/images/logos/rubbl-rewards-logo.png",
    shop: "/assets/images/logos/rubbl-shop-logo.png",
  };

  private readonly productLogosLight: Record<string, string> = {
    connect: "https://company.rubbl.com/pubweb/marketplace/images/logos/rubbl_connect_color_reverse.png",
  };

  get productLogoUrl(): string | null {
    const key = this.product.trim().toLowerCase();
    if (this.light && this.productLogosLight[key]) {
      return this.productLogosLight[key];
    }
    return this.productLogos[key] || null;
  }

  get altText(): string {
    return this.product ? `Rubbl ${this.product}` : "Rubbl";
  }
}
